import * as types from './constant';
import initialState from './initialState';
import { getLastArtifactKey } from '../utils/artifacts';

const agentHubReducer = (state = initialState, action) => {
  switch (action.type) {
    case types.FETCH_AGENTS_REQUEST:
      return {
        ...state,
        loading: true,
        error: null,
      };

    case types.FETCH_AGENTS_SUCCESS:
      return {
        ...state,
        loading: false,
        agents: action.payload,
        error: null,
      };

    case types.FETCH_AGENTS_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.payload,
      };

    case types.SELECT_AGENT:
      return {
        ...state,
        currentAgent: action.payload,
        messages: [],
        currentSessionId: null,
        artifacts: [],
        artifactPanelOpen: false,
        selectedArtifactIdentifier: null,
      };

    case types.FETCH_CONVERSATIONS_REQUEST:
      return {
        ...state,
        conversationsLoading: true,
        conversationsError: null,
      };

    case types.FETCH_CONVERSATIONS_SUCCESS:
      return {
        ...state,
        conversationsLoading: false,
        conversations: action.payload,
        conversationsError: null,
      };

    case types.FETCH_CONVERSATIONS_FAILURE:
      return {
        ...state,
        conversationsLoading: false,
        conversationsError: action.payload,
      };

    case types.SELECT_CONVERSATION:
      return {
        ...state,
        currentSessionId: action.payload.id,
        currentAgent: state.agents.find(a => a.id === action.payload.agentId) || state.currentAgent,
        artifacts: [],
        artifactPanelOpen: false,
        selectedArtifactIdentifier: null,
      };

    case types.FETCH_CONVERSATION_MESSAGES_REQUEST:
      return {
        ...state,
        messagesLoading: true,
        messagesError: null,
      };

    case types.FETCH_CONVERSATION_MESSAGES_SUCCESS: {
      const allArtifacts = action.payload.flatMap(msg => msg.artifacts || []);
      return {
        ...state,
        messagesLoading: false,
        messages: action.payload,
        messagesError: null,
        artifacts: allArtifacts,
        selectedArtifactIdentifier: getLastArtifactKey(allArtifacts),
      };
    }

    case types.FETCH_CONVERSATION_MESSAGES_FAILURE:
      return {
        ...state,
        messagesLoading: false,
        messagesError: action.payload,
      };

    case types.SET_SEARCH_TERM:
      return {
        ...state,
        searchTerm: action.payload,
      };

    case types.SET_FILTER_STATUS:
      return {
        ...state,
        filterStatus: action.payload,
      };

    case types.TOGGLE_MOBILE_MENU:
      return {
        ...state,
        isMobileOpen: !state.isMobileOpen,
      };

    case types.TOGGLE_COLLAPSE:
      return {
        ...state,
        isCollapsed: !state.isCollapsed,
      };

    case types.NAVIGATE_TO_VIEW:
      return {
        ...state,
        activeView: action.payload,
        isMobileOpen: false,
      };

    case types.SET_DARK_MODE:
      if (state.isDarkMode === action.payload) return state;
      return {
        ...state,
        isDarkMode: action.payload,
      };

    case types.SET_INPUT_MESSAGE:
      return {
        ...state,
        inputMessage: action.payload,
      };

    case types.SEND_MESSAGE:
      return {
        ...state,
        messages: [...state.messages, action.payload],
        inputMessage: '',
      };

    case types.RECEIVE_MESSAGE:
      return {
        ...state,
        messages: [...state.messages, action.payload],
      };

    case types.SET_IS_TYPING:
      return {
        ...state,
        isTyping: action.payload,
      };

    case types.RESET_CHAT:
      return {
        ...state,
        currentAgent: null,
        messages: [],
        inputMessage: '',
        isTyping: false,
        activeView: 'hub',
        currentSessionId: null,
        artifacts: [],
        artifactPanelOpen: false,
        selectedArtifactIdentifier: null,
      };

    case types.SET_SESSION_ID:
      return {
        ...state,
        currentSessionId: action.payload,
      };

    case types.WS_CONNECT_REQUEST:
      return {
        ...state,
        wsConnecting: true,
        wsError: null,
      };

    case types.WS_CONNECT_SUCCESS:
      return {
        ...state,
        wsConnected: true,
        wsConnecting: false,
        wsError: null,
      };

    case types.WS_CONNECT_FAILURE:
      return {
        ...state,
        wsConnected: false,
        wsConnecting: false,
        wsError: action.payload,
      };

    case types.WS_DISCONNECT:
      return {
        ...state,
        wsConnected: false,
        wsConnecting: false,
      };

    case types.WS_MESSAGE_CHUNK:
      return {
        ...state,
        streamingMessage: state.streamingMessage
          ? state.streamingMessage + action.payload
          : action.payload,
        isTyping: true,
      };

    case types.WS_MESSAGE_COMPLETE: {
      const newArtifacts = action.payload.metadata?.artifacts || [];
      const hasArtifacts = newArtifacts.length > 0;
      const updatedArtifacts = hasArtifacts
        ? [...state.artifacts, ...newArtifacts]
        : state.artifacts;

      return {
        ...state,
        messages: [
          ...state.messages,
          {
            id: action.payload.metadata?.conversationId || Date.now().toString(),
            text: action.payload.response,
            sender: 'agent',
            timestamp: new Date().toISOString(),
            metadata: action.payload.metadata,
            artifacts: newArtifacts,
          },
        ],
        streamingMessage: null,
        isTyping: false,
        artifacts: updatedArtifacts,
        artifactPanelOpen: hasArtifacts ? true : state.artifactPanelOpen,
        selectedArtifactIdentifier: hasArtifacts
          ? getLastArtifactKey(updatedArtifacts)
          : state.selectedArtifactIdentifier,
        artifactsEnabled: action.payload.metadata?.artifactsEnabled || state.artifactsEnabled,
        supportType: action.payload.metadata?.supportType || state.supportType,
      };
    }

    case types.WS_ERROR:
      return {
        ...state,
        wsError: action.payload,
        isTyping: false,
        streamingMessage: null,
      };

    case types.SET_ARTIFACTS:
      return {
        ...state,
        artifacts: action.payload,
        selectedArtifactIdentifier: getLastArtifactKey(action.payload),
      };

    case types.ADD_ARTIFACT:
      return {
        ...state,
        artifacts: [...state.artifacts, action.payload],
        artifactPanelOpen: true,
        selectedArtifactIdentifier: getLastArtifactKey([...state.artifacts, action.payload]),
      };

    case types.SET_SELECTED_ARTIFACT:
      return {
        ...state,
        selectedArtifactIdentifier: action.payload,
      };

    case types.TOGGLE_ARTIFACT_PANEL:
      return {
        ...state,
        artifactPanelOpen: action.payload !== undefined ? action.payload : !state.artifactPanelOpen,
      };

    case types.CLEAR_ARTIFACTS:
      return {
        ...state,
        artifacts: [],
        artifactPanelOpen: false,
        selectedArtifactIdentifier: null,
      };

    default:
      return state;
  }
};

export default agentHubReducer;
